/** @jsxImportSource @emotion/react */
import React, { useState, useMemo } from 'react';
import { css } from '@emotion/react';

import { useTranslation } from 'react-i18next';

import { DateRange, DefinedRange, defaultStaticRanges } from 'react-date-range';
import * as locales from 'react-date-range/dist/locale';

import Button from '@mui/material/Button';

import Popover from '@mui/material/Popover';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file

const labelMap = {
  Today: 'Idag',
  Yesterday: 'Igår',
  'This Week': 'Denna vecka',
  'Last Week': 'Förra veckan',
  'This Month': 'Denna månad',
  'Last Month': 'Förra månaden',
};

export default function DateRangeCustom({ selectionRange, onRangeChange }) {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const { t, i18n } = useTranslation();

  function handleSelect(ranges) {
    onRangeChange(ranges.selection);
  }

  const label = useMemo(() => {
    const find = defaultStaticRanges.find((x) => x.isSelected(selectionRange));
    if (find) {
      return t(labelMap[find.label]);
    }
    if (
      selectionRange.startDate &&
      selectionRange.endDate &&
      selectionRange.startDate.getTime() !== new Date('2023-01-01').getTime()
    ) {
      return `${selectionRange.startDate.toLocaleDateString(
        i18n.language
      )} - ${selectionRange.endDate.toLocaleDateString(i18n.language)}`;
    }
    return t('Senast ändrad');
  }, [t, selectionRange]);

  return (
    <div>
      <Button
        onClick={(e) => {
          setOpen(true);
          setAnchorEl(e.currentTarget);
        }}
        variant="outlined"
        color="inherit"
        css={css`
          padding-top: 8px;
          padding-bottom: 8px;
          border-color: #bdbdbd;
          color: #666666;
          font-weight: 400;
        `}
        endIcon={<ArrowDropDownIcon />}
      >
        {label}
      </Button>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <div
          css={css`
            display: flex;
          `}
        >
          <DefinedRange
            ranges={[selectionRange]}
            onChange={handleSelect}
            inputRanges={[]}
            staticRanges={defaultStaticRanges
              .map((x) => {
                return {
                  ...x,
                  label: t(labelMap[x.label]),
                };
              })
              .concat({
                label: t('Sedan start'),
                isSelected: () =>
                  !selectionRange?.startDate ||
                  selectionRange.startDate.getTime() ===
                    new Date('2023-01-01').getTime(),
                range: () => ({
                  startDate: new Date('2023-01-01'),
                  endDate: new Date(),
                }),
              })}
          />
          <DateRange
            ranges={[selectionRange]}
            onChange={handleSelect}
            moveRangeOnFirstSelection={false}
            showSelectionPreview={true}
            locale={locales?.[i18n.language]?.[i18n.language]}
            endDatePlaceholder={t('Slutdatum')}
            startDatePlaceholder={t('Startdatum')}
            day
          />
        </div>
      </Popover>
    </div>
  );
}
